import { useCallback, useMemo, useState } from "react";
import { TodoList } from "../../../../types/types";
import { TodoListLocalRepository } from "./repository/TodoListRepository";
import { WorkspaceLocalRepository } from "./repository/WorkspaceRepository";
import { TodoLocalRepository } from "./repository/TodoRepository";

const useTodoList_Local = (currentWorkspaceId: string | null, refreshWorkspace: () => void) => {
    const [todoLists, setTodoLists] = useState<TodoList[]>([]);
    const [todoListsMap, setTodoListsMap] = useState<Map<string, TodoList>>(new Map());

    const todoListRepository = useMemo(() => new TodoListLocalRepository(), []);
    const workspaceRepository = useMemo(() => new WorkspaceLocalRepository(), []);
    const todoRepository = useMemo(() => new TodoLocalRepository(), []);

    const createTodoList = async (title?: string): Promise<TodoList> => {
        if (!currentWorkspaceId) throw new Error("No workspace selected");

        const newList: TodoList = {
            id: crypto.randomUUID(),
            workspaceId: currentWorkspaceId,
            title: title ?? 'New List',
            todos: [],
        };

        await todoListRepository.save(newList);

        const workspace = await workspaceRepository.findById(currentWorkspaceId);
        if (workspace) {
            workspace.lists = [newList.id, ...workspace.lists];
            await workspaceRepository.save(workspace);
        }

        await refresh();
        refreshWorkspace();
        return newList;
    };

    const getTodoLists = useCallback(async (): Promise<TodoList[]> => {
        if (!currentWorkspaceId) return [];
        const map: Map<string, TodoList> = new Map(await todoListRepository.findAll().map(list => [list.id, list]));
        const listsIds = await workspaceRepository.findById(currentWorkspaceId);
        const lists = listsIds?.lists
            .map(id => map.get(id))
            .filter((list): list is TodoList => list !== undefined && list !== null) ?? [];
        return lists;
    }, [currentWorkspaceId, todoListRepository, workspaceRepository]);

    const updateListTitle = async (todoListId: string, title: string): Promise<boolean> => {
        const list = await todoListRepository.findById(todoListId);
        if (!list) return false;

        list.title = title;
        await todoListRepository.save(list);
        await refresh();
        return true;
    };

    const deleteTodoList = async (todoListId: string): Promise<boolean> => {
        const todos = await todoRepository.findAllByListId(todoListId);
        await todoRepository.deleteAll(todos);
        await todoListRepository.delete(todoListId);

        if (currentWorkspaceId) {
            const workspace = await workspaceRepository.findById(currentWorkspaceId);
            if (workspace) {
                workspace.lists = workspace.lists.filter(id => id !== todoListId);
                await workspaceRepository.save(workspace);
            }
        }

        await refresh();
        refreshWorkspace();
        return true;
    };

    const reorderList = async (sourceIndex: number, destinationIndex: number): Promise<boolean> => {
        if (!currentWorkspaceId) return false;

        const workspace = await workspaceRepository.findById(currentWorkspaceId);
        if (!workspace) return false;

        const lists = [...workspace.lists];
        const [removed] = lists.splice(sourceIndex, 1);
        lists.splice(destinationIndex, 0, removed);

        workspace.lists = lists;
        await workspaceRepository.save(workspace);

        await refresh();
        refreshWorkspace();
        return true;
    };

    const refresh = useCallback(async () => {
        try {
            const lists = await getTodoLists();
            setTodoLists(lists);
            setTodoListsMap(new Map(lists.map(list => [list.id, list])));
        } catch (error) {
            // console.error("Error refreshing todo lists:");
        }
    }, [getTodoLists]);

    const clear = useCallback(async () => {
        await todoListRepository.clear();
        setTodoLists([]);
        setTodoListsMap(new Map());
    }, [todoListRepository]);

    return {
        refresh,
        clear,
        todoLists,
        todoListsMap,
        createTodoList,
        updateListTitle,
        reorderList,
        deleteTodoList,
    }
}

export default useTodoList_Local;